import React, { useState, useRef, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../services/api";
import { toast, ToastContainer } from "react-toastify";

const ExamLayer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const examId = searchParams.get("id");

  // Check if we're editing an existing exam
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [practiceQuestionsLoading, setPracticeQuestionsLoading] =
    useState(false);
  const [practiceQuestions, setPracticeQuestions] = useState([]);
  const [filteredPracticeQuestions, setFilteredPracticeQuestions] = useState(
    []
  );
  const [categoryFilter, setCategoryFilter] = useState("");
  const [subcategoryFilter, setSubcategoryFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const examCategories = [
    { id: "BASIC", name: "Basic", duration: 30 },
    { id: "MEDIUM", name: "Medium", duration: 45 },
    { id: "ADVANCED", name: "Advanced", duration: 60 },
    { id: "EXPERT", name: "Expert", duration: 90 },
  ];

  // State for categories from database
  const [dbCategories, setDbCategories] = useState([]);
  const [dbSubcategories, setDbSubcategories] = useState({});

  // Fetch available practice questions
  useEffect(() => {
    const fetchPracticeQuestions = async () => {
      try {
        setPracticeQuestionsLoading(true);
        const response = await api.practiceQuestions.getAll();

        if (response.success) {
          setPracticeQuestions(response.data);
          setFilteredPracticeQuestions(response.data);
        } else {
          toast.error(response.message || "Failed to fetch practice questions");
        }
      } catch (error) {
        console.error("Error fetching practice questions:", error);
        toast.error("An error occurred while fetching practice questions");
      } finally {
        setPracticeQuestionsLoading(false);
      }
    };

    fetchPracticeQuestions();
  }, []);

  // Filter practice questions based on category, subcategory, and search query
  useEffect(() => {
    let filtered = [...practiceQuestions];

    if (categoryFilter) {
      filtered = filtered.filter((q) => q.categoryId === categoryFilter);
    }

    if (subcategoryFilter) {
      filtered = filtered.filter((q) => q.subcategoryId === subcategoryFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (q) =>
          q.questionText.toLowerCase().includes(query) ||
          q.options.some((opt) => opt.toLowerCase().includes(query))
      );
    }

    setFilteredPracticeQuestions(filtered);
  }, [practiceQuestions, categoryFilter, subcategoryFilter, searchQuery]);

  // Fetch categories and subcategories from the database
  useEffect(() => {
    const fetchCategoriesAndSubcategories = async () => {
      try {
        setCategoriesLoading(true);
        const response = await api.categories.getAll();

        if (response.success) {
          setDbCategories(response.data);

          // Create a mapping of category ID to its subcategories
          const subcategoriesMap = {};
          for (const category of response.data) {
            subcategoriesMap[category.id] = category.subcategories || [];
          }

          setDbSubcategories(subcategoriesMap);
        } else {
          toast.error(response.message || "Failed to fetch categories");
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("An error occurred while fetching categories");
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategoriesAndSubcategories();
  }, []);

  const fileInputRef = useRef(null);

  const [examData, setExamData] = useState({
    title: "",
    description: "",
    duration: 30,
    passPercentage: 70,
    difficulty: "BASIC",
    isActive: true,
    image: "",
  });

  const [questions, setQuestions] = useState([
    {
      id: 1,
      questionText: "",
      options: ["", "", "", ""],
      correctAnswer: 0,
      points: 1,
      category: "",
      subcategory: "",
      explanation: "",
      categoryId: "",
      subcategoryId: "",
    },
  ]);

  // State for selected practice questions
  const [selectedPracticeQuestions, setSelectedPracticeQuestions] = useState(
    []
  );

  // State to track points for each practice question
  const [practiceQuestionPoints, setPracticeQuestionPoints] = useState({});

  // Add a practice question to the exam
  const addPracticeQuestionToExam = (practiceQuestion, points = 1) => {
    // Check if the question is already in the exam
    const isAlreadyAdded = questions.some(
      (q) => q.questionId === practiceQuestion.id
    );

    if (isAlreadyAdded) {
      toast.warning("This question is already in the exam");
      return;
    }

    // Create a new question object from the practice question
    const newQuestion = {
      id: questions.length + 1,
      questionId: practiceQuestion.id, // Store the actual practice question ID
      questionText: practiceQuestion.questionText,
      options: practiceQuestion.options,
      correctAnswer: practiceQuestion.correctAnswer,
      points: points, // Use provided points or default to 1
      category: practiceQuestion.category?.name || "",
      subcategory: practiceQuestion.subcategory?.name || "",
      explanation: practiceQuestion.explanation || "",
      categoryId: practiceQuestion.categoryId,
      subcategoryId: practiceQuestion.subcategoryId,
    };

    // Add the question to the exam
    setQuestions([...questions, newQuestion]);

    // Add to selected questions list
    setSelectedPracticeQuestions([
      ...selectedPracticeQuestions,
      practiceQuestion.id,
    ]);

    // Update points for this question
    updatePracticeQuestionPoints(practiceQuestion.id, points);

    toast.success("Question added to exam");
  };

  // Update points for a practice question
  const updatePracticeQuestionPoints = (questionId, points) => {
    setPracticeQuestionPoints({
      ...practiceQuestionPoints,
      [questionId]: points,
    });
  };

  // Remove a practice question from selected list when removed from exam
  const removeQuestionAndUpdateSelected = (index) => {
    const updatedQuestions = [...questions];
    const removedQuestion = updatedQuestions[index];

    updatedQuestions.splice(index, 1);
    setQuestions(updatedQuestions);

    // Update selected questions list
    if (removedQuestion.questionId) {
      setSelectedPracticeQuestions(
        selectedPracticeQuestions.filter(
          (id) => id !== removedQuestion.questionId
        )
      );
    }
  };

  const [csvError, setCsvError] = useState(null);

  // Fetch exam data if editing
  useEffect(() => {
    const fetchExamData = async () => {
      if (examId) {
        try {
          setInitialLoading(true);
          setIsEditing(true);

          const response = await api.exams.getById(examId);

          if (response.success) {
            const exam = response.data;

            // Set exam data
            setExamData({
              title: exam.title || "",
              description: exam.description || "",
              duration: exam.duration || 30,
              passPercentage: exam.passPercentage || 70,
              difficulty: exam.difficulty || "BASIC",
              isActive: exam.isActive !== undefined ? exam.isActive : true,
              image: exam.image || "",
            });

            // If the exam has questions, fetch them
            if (exam.questions && exam.questions.length > 0) {
              const questionsResponse = await api.exams.getExamQuestions(
                examId
              );

              if (questionsResponse.success) {
                const formattedQuestions = questionsResponse.data.map(
                  (item, index) => {
                    const q = item.question;
                    return {
                      id: index + 1,
                      questionId: q.id, // Store the actual question ID for update
                      questionText: q.questionText || "",
                      options: q.options || ["", "", "", ""],
                      correctAnswer: q.correctAnswer || 0,
                      points: item.points || 1, // Get points from the join table
                      category: q.category?.name || "",
                      subcategory: q.subcategory?.name || "",
                      explanation: q.explanation || "",
                      categoryId: q.categoryId || "",
                      subcategoryId: q.subcategoryId || "",
                    };
                  }
                );

                // Set the questions
                setQuestions(
                  formattedQuestions.length > 0 ? formattedQuestions : questions
                );

                // Set the selected practice questions for the UI
                const selectedIds = formattedQuestions.map((q) => q.questionId);
                setSelectedPracticeQuestions(selectedIds);
              }
            }
          } else {
            toast.error(response.message || "Failed to fetch exam");
            navigate("/all-exams");
          }
        } catch (err) {
          toast.error("Error connecting to the server");
          console.error("Error fetching exam:", err);
          navigate("/all-exams");
        } finally {
          setInitialLoading(false);
        }
      }
    };

    fetchExamData();
  }, [examId, navigate]);

  const handleExamDataChange = (e) => {
    const { name, value } = e.target;
    setExamData({
      ...examData,
      [name]: value,
    });

    // If category changes, update the duration accordingly
    if (name === "difficulty") {
      const selectedCategory = examCategories.find(
        (category) => category.id === value
      );
      if (selectedCategory) {
        setExamData((prevData) => ({
          ...prevData,
          [name]: value,
          duration: selectedCategory.duration,
        }));
      }
    }
  };

  const handleQuestionChange = (index, e) => {
    const { name, value } = e.target;
    const updatedQuestions = [...questions];

    if (name === "category") {
      // Find the category object by name
      const selectedCategory = dbCategories.find((cat) => cat.id === value);

      updatedQuestions[index].category = selectedCategory
        ? selectedCategory.name
        : "";
      updatedQuestions[index].categoryId = value;

      // Reset subcategory when category changes
      updatedQuestions[index].subcategory = "";
      updatedQuestions[index].subcategoryId = "";
    } else if (name === "subcategory") {
      // Find the subcategory object by ID
      const categoryId = updatedQuestions[index].categoryId;
      const selectedSubcategory = dbSubcategories[categoryId]?.find(
        (sub) => sub.id === value
      );

      updatedQuestions[index].subcategory = selectedSubcategory
        ? selectedSubcategory.name
        : "";
      updatedQuestions[index].subcategoryId = value;
    } else {
      updatedQuestions[index][name] = value;
    }

    setQuestions(updatedQuestions);
  };

  const handleOptionChange = (questionIndex, optionIndex, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].options[optionIndex] = value;
    setQuestions(updatedQuestions);
  };

  const handleCorrectAnswerChange = (questionIndex, optionIndex) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].correctAnswer = optionIndex;
    setQuestions(updatedQuestions);
  };

  const addQuestion = () => {
    setQuestions([
      ...questions,
      {
        id: questions.length + 1,
        questionText: "",
        options: ["", "", "", ""],
        correctAnswer: 0,
        points: 1,
        category: "",
        subcategory: "",
        explanation: "",
        categoryId: "",
        subcategoryId: "",
      },
    ]);
  };

  const removeQuestion = (index) => {
    const updatedQuestions = [...questions];
    updatedQuestions.splice(index, 1);
    setQuestions(updatedQuestions);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate form data
      if (!examData.title.trim()) {
        toast.error("Exam title is required");
        setLoading(false);
        return;
      }

      // Validate questions
      for (let i = 0; i < questions.length; i++) {
        const q = questions[i];
        if (!q.questionText.trim()) {
          toast.error(`Question ${i + 1} text is required`);
          setLoading(false);
          return;
        }

        if (!q.categoryId) {
          toast.error(`Category for Question ${i + 1} is required`);
          setLoading(false);
          return;
        }

        if (!q.subcategoryId) {
          toast.error(`Subcategory for Question ${i + 1} is required`);
          setLoading(false);
          return;
        }

        for (let j = 0; j < q.options.length; j++) {
          if (!q.options[j].trim()) {
            toast.error(`Option ${j + 1} for Question ${i + 1} is required`);
            setLoading(false);
            return;
          }
        }
      }

      // Prepare data for API
      const examPayload = {
        title: examData.title,
        description: examData.description,
        duration: parseInt(examData.duration),
        passPercentage: parseInt(examData.passPercentage),
        difficulty: examData.difficulty,
        isActive: examData.isActive,
        image: examData.image,
        totalQuestions: questions.length, // Set the correct total questions count
      };

      let examResponse;

      // Create or update exam
      if (isEditing) {
        examResponse = await api.exams.update(examId, examPayload);
      } else {
        examResponse = await api.exams.create(examPayload);
      }

      if (!examResponse.success) {
        toast.error(examResponse.message || "Failed to save exam");
        setLoading(false);
        return;
      }

      const createdExam = examResponse.data;

      // Store the exam ID, either from the URL param (for editing) or from the newly created exam
      const currentExamId = isEditing ? examId : createdExam.id;

      // Process questions
      const questionsPromises = questions.map(async (question) => {
        // For questions that already have a questionId, they are existing practice questions
        if (question.questionId) {
          // For existing practice questions, we just need to add or update the connection
          // with points in the join table
          return await api.exams.updateQuestion(question.questionId, {
            examId: currentExamId,
            points: parseInt(question.points),
          });
        } else {
          // For new questions, create them as practice questions
          const questionData = {
            questionText: question.questionText,
            options: question.options,
            correctAnswer: question.correctAnswer,
            explanation: question.explanation,
            categoryId: question.categoryId,
            subcategoryId: question.subcategoryId,
            examId: currentExamId, // Add the exam ID to link the question to the exam
            points: parseInt(question.points), // Points for this question in the exam
          };

          return await api.exams.createQuestion(questionData);
        }
      });

      // Wait for all question operations to complete
      const questionResults = await Promise.all(questionsPromises);

      // After all questions are created, update the exam's total question count to make sure it's accurate
      if (questionResults.length > 0) {
        await api.exams.update(currentExamId, {
          totalQuestions: questions.length,
        });
      }

      toast.success(`Exam ${isEditing ? "updated" : "created"} successfully!`);

      // Redirect to all exams page
      setTimeout(() => {
        navigate("/all-exams");
      }, 2000);
    } catch (err) {
      toast.error("Error connecting to the server");
      console.error("Error saving exam:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    setCsvError(null);

    if (!file) {
      return;
    }

    // Check file extension
    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (fileExtension !== "csv") {
      setCsvError("Please upload a CSV file.");
      e.target.value = null;
      return;
    }

    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const csvData = event.target.result;
        const lines = csvData.split("\n");

        // Filter out empty lines
        const nonEmptyLines = lines.filter((line) => line.trim() !== "");

        // Parse CSV lines
        const parsedQuestions = nonEmptyLines.map((line, index) => {
          const values = line.split(",").map((value) => value.trim());

          // Check if there are enough values
          if (values.length < 8) {
            throw new Error(
              `Line ${
                index + 1
              } does not have enough values. Format: question,option1,option2,option3,option4,correctOptionIndex,categoryName,subcategoryName`
            );
          }

          const [
            questionText,
            option1,
            option2,
            option3,
            option4,
            correctAnswerStr,
            categoryName,
            subcategoryName,
            explanation,
          ] = values;
          const correctAnswer = parseInt(correctAnswerStr, 10);

          // Validate correctAnswer is a number between 0-3
          if (isNaN(correctAnswer) || correctAnswer < 0 || correctAnswer > 3) {
            throw new Error(
              `Line ${
                index + 1
              } has an invalid correctOptionIndex. It should be a number between 0 and 3.`
            );
          }

          // Find category and subcategory by name
          const category = dbCategories.find(
            (cat) => cat.name === categoryName
          );
          if (!category) {
            throw new Error(
              `Line ${index + 1} has an invalid category name: ${categoryName}`
            );
          }

          const subcategory = dbSubcategories[category.id]?.find(
            (sub) => sub.name === subcategoryName
          );
          if (!subcategory) {
            throw new Error(
              `Line ${
                index + 1
              } has an invalid subcategory name: ${subcategoryName} for category: ${
                category.name
              }`
            );
          }

          return {
            id: questions.length + index + 1,
            questionText,
            options: [option1, option2, option3, option4],
            correctAnswer,
            points: 1,
            category: category.name,
            subcategory: subcategory.name,
            explanation: explanation || "",
            categoryId: category.id,
            subcategoryId: subcategory.id,
          };
        });

        // Add the parsed questions to the existing questions
        setQuestions([...questions, ...parsedQuestions]);
        toast.success(
          `${parsedQuestions.length} questions imported successfully`
        );
        e.target.value = null;
      } catch (error) {
        setCsvError(error.message);
        e.target.value = null;
      }
    };

    reader.readAsText(file);
  };

  const downloadSampleCsv = () => {
    // Check if we have categories loaded
    if (dbCategories.length === 0) {
      toast.error(
        "Please wait for categories to load before downloading the sample"
      );
      return;
    }

    // Get a sample category and subcategory
    const sampleCategory = dbCategories[0];
    const sampleSubcategory = dbSubcategories[sampleCategory.id]?.[0];

    if (!sampleCategory || !sampleSubcategory) {
      toast.error(
        "No categories or subcategories available. Please add them first."
      );
      return;
    }

    const sampleData = [
      [
        "What is the primary function of a safety harness?",
        "Fall protection",
        "Fire protection",
        "Electrical protection",
        "Chemical protection",
        "0",
        sampleCategory.name,
        sampleSubcategory.name,
        "Safety harnesses are designed to prevent falls from height and distribute the impact forces safely.",
      ],
      [
        "What is the first step in risk assessment?",
        "Identify hazards",
        "Implement controls",
        "Evaluate risks",
        "Monitor results",
        "0",
        sampleCategory.name,
        sampleSubcategory.name,
        "Identifying hazards is always the first step in any risk assessment process.",
      ],
    ];

    // Convert to CSV format
    const csvContent = sampleData.map((row) => row.join(",")).join("\n");

    // Create a blob and download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "sample-questions.csv");
    document.body.appendChild(link);

    // Trigger download
    link.click();
    document.body.removeChild(link);
  };

  if (initialLoading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div
                className="card-body p-24 d-flex justify-content-center align-items-center"
                style={{ minHeight: "400px" }}
              >
                <div className="text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Loading exam data...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header border-bottom bg-base py-16 px-24">
              <h6 className="text-lg fw-semibold mb-0">
                {isEditing ? "Edit Exam" : "Create New Exam"}
              </h6>
            </div>
            <div className="card-body p-24">
              <form onSubmit={handleSubmit}>
                {/* Exam Details */}
                <div className="border p-3 rounded mb-4">
                  <h6 className="mb-3 fw-semibold">Exam Details</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Exam Title</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={examData.title}
                        onChange={handleExamDataChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Category</label>
                      <select
                        className="form-select"
                        name="difficulty"
                        value={examData.difficulty}
                        onChange={handleExamDataChange}
                      >
                        {examCategories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Duration (minutes)</label>
                      <input
                        type="number"
                        className="form-control"
                        name="duration"
                        value={examData.duration}
                        onChange={handleExamDataChange}
                        min="1"
                        max="180"
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Pass Percentage (%)</label>
                      <input
                        type="number"
                        className="form-control"
                        name="passPercentage"
                        value={examData.passPercentage}
                        onChange={handleExamDataChange}
                        min="1"
                        max="100"
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        name="isActive"
                        value={examData.isActive}
                        onChange={(e) =>
                          setExamData({
                            ...examData,
                            isActive: e.target.value === "true",
                          })
                        }
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                    <div className="col-md-12">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        name="description"
                        value={examData.description}
                        onChange={handleExamDataChange}
                        rows="3"
                      ></textarea>
                    </div>
                  </div>
                </div>

                {/* Questions Section */}
                <div className="border p-3 rounded mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0 fw-semibold">
                      Questions ({questions.length})
                    </h6>
                    <div>
                      <input
                        type="file"
                        accept=".csv"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        style={{ display: "none" }}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm me-2"
                        onClick={downloadSampleCsv}
                      >
                        <Icon
                          icon="heroicons:document-arrow-down"
                          className="me-1"
                        />
                        Download Sample CSV
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-success btn-sm"
                        onClick={() => fileInputRef.current.click()}
                      >
                        <Icon icon="heroicons:document-plus" className="me-1" />
                        Import Questions
                      </button>
                    </div>
                  </div>

                  {csvError && (
                    <div className="alert alert-danger mb-3">{csvError}</div>
                  )}

                  {/* CSV Import Instructions */}
                  <div className="alert alert-info mb-3 small">
                    <h6 className="mb-2">CSV Format Instructions:</h6>
                    <p className="mb-1">
                      Your CSV file should have the following format:
                    </p>
                    <pre className="bg-light p-2 rounded mb-2 small">
                      questionText,option1,option2,option3,option4,correctAnswerIndex,categoryName,subcategoryName
                    </pre>
                    <ul className="mb-0 ps-3">
                      <li>
                        <strong>correctAnswerIndex:</strong> 0-3 (0 = option1, 1
                        = option2, etc.)
                      </li>
                      <li>
                        <strong>categoryName:</strong> The name of the category
                      </li>
                      <li>
                        <strong>subcategoryName:</strong> The name of the
                        subcategory
                      </li>
                      <li>
                        <strong>explanation:</strong> Optional explanation of
                        the answer
                      </li>
                    </ul>
                    <p className="mt-2 mb-0">
                      <strong>Note:</strong> Download the sample CSV to see the
                      correct format with valid category and subcategory names.
                    </p>
                  </div>

                  {/* Exam Questions */}
                  {questions.map((question, index) => (
                    <div
                      key={question.id}
                      className="border rounded p-3 mb-3 bg-body-tertiary"
                    >
                      <div className="d-flex justify-content-between mb-3">
                        <h6 className="mb-0">
                          Question {index + 1}
                          {question.questionId && (
                            <span className="ms-2 badge bg-secondary">
                              ID: {question.questionId}
                            </span>
                          )}
                        </h6>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeQuestionAndUpdateSelected(index)}
                        >
                          <Icon icon="heroicons:trash" />
                        </button>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Question Text</label>
                        <textarea
                          className="form-control"
                          name="questionText"
                          value={question.questionText}
                          onChange={(e) => handleQuestionChange(index, e)}
                          rows="2"
                          required
                        ></textarea>
                      </div>

                      <div className="input-group mb-3">
                        <label
                          className="input-group-text bg-light"
                          htmlFor={`question-${index}-category`}
                        >
                          Category
                        </label>
                        <select
                          className="form-select"
                          id={`question-${index}-category`}
                          name="category"
                          value={question.categoryId}
                          onChange={(e) => handleQuestionChange(index, e)}
                          required
                        >
                          <option value="">Select Category</option>
                          {dbCategories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="input-group mb-3">
                        <label
                          className="input-group-text bg-light"
                          htmlFor={`question-${index}-subcategory`}
                        >
                          Subcategory
                        </label>
                        <select
                          className="form-select"
                          id={`question-${index}-subcategory`}
                          name="subcategory"
                          value={question.subcategoryId}
                          onChange={(e) => handleQuestionChange(index, e)}
                          required
                          disabled={!question.categoryId}
                        >
                          <option value="">Select Subcategory</option>
                          {dbSubcategories[question.categoryId]?.map(
                            (subcat) => (
                              <option key={subcat.id} value={subcat.id}>
                                {subcat.name}
                              </option>
                            )
                          )}
                        </select>
                      </div>

                      <div className="row mb-3">
                        <div className="col-md-3">
                          <label className="form-label">Points</label>
                          <input
                            type="number"
                            className="form-control"
                            name="points"
                            value={question.points}
                            onChange={(e) => handleQuestionChange(index, e)}
                            min="1"
                            max="10"
                          />
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Options</label>
                        {question.options.map((option, optionIndex) => (
                          <div key={optionIndex} className="input-group mb-2">
                            <div className="input-group-text">
                              <input
                                type="radio"
                                className="form-check-input mt-0"
                                checked={question.correctAnswer === optionIndex}
                                onChange={() =>
                                  handleCorrectAnswerChange(index, optionIndex)
                                }
                                aria-label="Correct answer"
                              />
                            </div>
                            <input
                              type="text"
                              className="form-control"
                              placeholder={`Option ${optionIndex + 1}`}
                              value={option}
                              onChange={(e) =>
                                handleOptionChange(
                                  index,
                                  optionIndex,
                                  e.target.value
                                )
                              }
                              required
                            />
                          </div>
                        ))}
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          Explanation (Optional)
                        </label>
                        <textarea
                          className="form-control"
                          name="explanation"
                          value={question.explanation}
                          onChange={(e) => handleQuestionChange(index, e)}
                          rows="2"
                          placeholder="Explain why the correct answer is right"
                        ></textarea>
                      </div>
                    </div>
                  ))}

                  {/* Button to add a blank question */}
                  <div className="text-center mb-4">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={addQuestion}
                    >
                      <Icon icon="heroicons:plus-circle" className="me-1" />
                      Add Blank Question
                    </button>
                  </div>
                </div>

                {/* Available Practice Questions Section */}
                <div className="border p-3 rounded mb-4">
                  <h6 className="mb-3 fw-semibold">
                    Available Practice Questions
                  </h6>

                  {/* Filters */}
                  <div className="row g-3 mb-3">
                    <div className="col-md-4">
                      <label className="form-label">Filter by Category</label>
                      <select
                        className="form-select"
                        value={categoryFilter}
                        onChange={(e) => {
                          setCategoryFilter(e.target.value);
                          setSubcategoryFilter(""); // Reset subcategory when category changes
                        }}
                      >
                        <option value="">All Categories</option>
                        {dbCategories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Filter by Subcategory
                      </label>
                      <select
                        className="form-select"
                        value={subcategoryFilter}
                        onChange={(e) => setSubcategoryFilter(e.target.value)}
                        disabled={!categoryFilter}
                      >
                        <option value="">All Subcategories</option>
                        {dbSubcategories[categoryFilter]?.map((subcat) => (
                          <option key={subcat.id} value={subcat.id}>
                            {subcat.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Search Questions</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by text..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Loading indicator */}
                  {practiceQuestionsLoading && (
                    <div className="text-center my-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">
                          Loading questions...
                        </span>
                      </div>
                      <p className="mt-2">Loading practice questions...</p>
                    </div>
                  )}

                  {/* No questions message */}
                  {!practiceQuestionsLoading &&
                    filteredPracticeQuestions.length === 0 && (
                      <div className="alert alert-info">
                        No practice questions match your filters. Try adjusting
                        your search criteria.
                      </div>
                    )}

                  {/* Practice questions list */}
                  {!practiceQuestionsLoading &&
                    filteredPracticeQuestions.length > 0 && (
                      <div className="list-group">
                        {filteredPracticeQuestions.map((q) => {
                          // Get points for this question from state, default to 1
                          const questionPoints =
                            practiceQuestionPoints[q.id] || 1;

                          return (
                            <div
                              key={q.id}
                              className="list-group-item list-group-item-action"
                            >
                              <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1">
                                  <h6 className="mb-1">{q.questionText}</h6>
                                  <div className="mb-2 small text-muted">
                                    Category: {q.category?.name || "Unknown"} |
                                    Subcategory:{" "}
                                    {q.subcategory?.name || "Unknown"} |
                                    Difficulty: {q.difficulty}
                                  </div>
                                  <div className="mb-1">
                                    <small className="fw-bold">Options:</small>
                                    <ul className="mb-0 ps-3">
                                      {q.options.map((opt, i) => (
                                        <li
                                          key={i}
                                          className={
                                            i === q.correctAnswer
                                              ? "text-success"
                                              : ""
                                          }
                                        >
                                          {opt}{" "}
                                          {i === q.correctAnswer && (
                                            <small>(correct)</small>
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                                <div className="d-flex flex-column align-items-end">
                                  {!selectedPracticeQuestions.includes(
                                    q.id
                                  ) && (
                                    <div
                                      className="input-group mb-2"
                                      style={{ width: "120px" }}
                                    >
                                      <span className="input-group-text">
                                        Points
                                      </span>
                                      <input
                                        type="number"
                                        className="form-control"
                                        min="1"
                                        max="10"
                                        value={questionPoints}
                                        onChange={(e) =>
                                          updatePracticeQuestionPoints(
                                            q.id,
                                            parseInt(e.target.value) || 1
                                          )
                                        }
                                      />
                                    </div>
                                  )}
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={() =>
                                      addPracticeQuestionToExam(
                                        q,
                                        questionPoints
                                      )
                                    }
                                    disabled={selectedPracticeQuestions.includes(
                                      q.id
                                    )}
                                  >
                                    {selectedPracticeQuestions.includes(
                                      q.id
                                    ) ? (
                                      <>
                                        <Icon
                                          icon="heroicons:check-circle"
                                          className="me-1"
                                        />
                                        Added
                                      </>
                                    ) : (
                                      <>
                                        <Icon
                                          icon="heroicons:plus-circle"
                                          className="me-1"
                                        />
                                        Add to Exam
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                  {/* Pagination could be added here in the future */}
                </div>

                {/* Submit Button */}
                <div className="d-flex justify-content-end mt-4">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    onClick={() => navigate("/all-exams")}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        {isEditing ? "Updating..." : "Creating..."}
                      </>
                    ) : isEditing ? (
                      "Update Exam"
                    ) : (
                      "Create Exam"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamLayer;
